import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getEstimates, updateEstimate, deleteEstimate } from '../../services/estimate-service';
import { getAssignableUsers } from '../../services/user-service';
import AppLayout from '../../components/AppLayout';
import Button from '../../components/ui/Button';
import {
  Search, Trash2, Loader2, ChevronRight, ChevronLeft,
  MapPin, Phone, ArrowRight, ClipboardCheck, CheckCircle2, Pencil, UserRound, Plus, Users,
} from 'lucide-react';

// ── Palette — one warm, coherent family instead of scattered SaaS-default hues.
const INK    = '#2B2620'; // primary text
const WALNUT = '#8B4513'; // brand anchor — matches sidebar/topbar app-wide
const MOSS   = '#3B6B44'; // approved / positive
const OCHRE  = '#A6752A'; // sent / in-progress
const RUST   = '#A13D2D'; // declined / warning
const STONE  = '#8C8275'; // draft / muted / borders
const PAPER  = '#FDFBF8'; // card surface — warm, not clinical white

// ── Type — Fraunces for headline numbers & money, Plex Sans for everything
// else. Loaded via index.html <link>.
const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const STATUS_CONFIG = {
  draft:    { label: 'Draft',    color: STONE, bg: '#F1EDE7' },
  sent:     { label: 'Sent',     color: OCHRE, bg: '#FBF1E0' },
  approved: { label: 'Approved', color: MOSS,  bg: '#EAF1EA' },
  declined: { label: 'Declined', color: RUST,  bg: '#FBEBE8' },
  archived: { label: 'Archived', color: STONE, bg: '#F5F3F0' },
};

const SORT_OPTIONS = [
  { value: 'newest',  label: 'Last worked on' },
  { value: 'oldest',  label: 'Oldest first'   },
  { value: 'highest', label: 'Highest amount' },
  { value: 'lowest',  label: 'Lowest amount'  },
  { value: 'name',    label: 'Customer A–Z'   },
];

// "Today, 2:41 PM" / "Oct 21, 2:41 PM" — shows reps exactly when their work was last kept
const formatSavedAt = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return `Today, ${time}`;
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return `Yesterday, ${time}`;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
};

// Plain-English contents of an estimate, e.g. "5 windows · 1 door · 2 other items"
function summarizeLines(lines = []) {
  const filled = lines.filter(l => l.type !== 'note'
    && ((l.description || '').trim() || parseFloat(l.price) > 0));
  const qty = l => parseFloat(l.qty) || 1;
  const sum = arr => arr.reduce((s, l) => s + qty(l), 0);
  const windows = sum(filled.filter(l => l.type === 'twp_window' || l.type === 'ordered_window'));
  const doors   = sum(filled.filter(l => l.type === 'door'));
  const other   = filled.filter(l => !['twp_window', 'ordered_window', 'door'].includes(l.type)).length;
  const parts = [];
  if (windows) parts.push(`${windows} window${windows === 1 ? '' : 's'}`);
  if (doors)   parts.push(`${doors} door${doors === 1 ? '' : 's'}`);
  if (other)   parts.push(`${other} other item${other === 1 ? '' : 's'}`);
  return parts.length ? parts.join(' · ') : 'Nothing added yet';
}

const fmtMoney = (n) =>
  (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ══════════════════════════════════════════════════════════════════════ */
/*  TWO-STEP DELETE BUTTON                                               */
/* ══════════════════════════════════════════════════════════════════════ */
function DeleteBtn({ onConfirm }) {
  const [armed, setArmed] = useState(false);
  const timerRef          = useRef(null);

  function handleClick(e) {
    e.stopPropagation();
    if (!armed) {
      setArmed(true);
      timerRef.current = setTimeout(() => setArmed(false), 2500);
    } else {
      clearTimeout(timerRef.current);
      setArmed(false);
      onConfirm(e);
    }
  }

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <button
      onClick={handleClick}
      title={armed ? 'Click again to confirm delete' : 'Delete estimate'}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: '6px', padding: '9px 14px',
        background: armed ? RUST : 'white',
        border: `1.5px solid ${armed ? RUST : '#E3B8AE'}`,
        borderRadius: '9px',
        color: armed ? 'white' : RUST,
        fontSize: '13px', fontWeight: '700',
        cursor: 'pointer', fontFamily: FONT_SANS,
        transition: 'all 0.15s', whiteSpace: 'nowrap',
      }}
    >
      {armed ? 'Click again to delete' : <><Trash2 size={15} strokeWidth={2} /> Delete</>}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  TWO-STEP APPROVE → JOB ORDER BUTTON                                  */
/*  Lets a not-yet-approved estimate be approved and jumped straight to  */
/*  Job Order creation right from its row — no need to open the editor.  */
/* ══════════════════════════════════════════════════════════════════════ */
function ApproveBtn({ onConfirm }) {
  const [armed, setArmed] = useState(false);
  const timerRef          = useRef(null);

  function handleClick(e) {
    e.stopPropagation();
    if (!armed) {
      setArmed(true);
      timerRef.current = setTimeout(() => setArmed(false), 2500);
    } else {
      clearTimeout(timerRef.current);
      setArmed(false);
      onConfirm(e);
    }
  }

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <button
      onClick={handleClick}
      title={armed ? 'Click again to approve & open Job Order' : 'Approve & Create Job Order'}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: '6px', padding: '9px 14px',
        background: armed ? MOSS : '#EAF1EA',
        border: `1.5px solid ${armed ? MOSS : '#B9D3BC'}`,
        borderRadius: '9px',
        color: armed ? 'white' : MOSS,
        fontSize: '13px', fontWeight: '700',
        cursor: 'pointer', fontFamily: FONT_SANS,
        transition: 'all 0.15s', whiteSpace: 'nowrap',
      }}
    >
      {armed
        ? <>Click again to approve <ArrowRight size={14} strokeWidth={2} /></>
        : <><CheckCircle2 size={15} strokeWidth={2} /> Approve</>}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  ESTIMATE ROW — one big, plain row per quote                          */
/* ══════════════════════════════════════════════════════════════════════ */
function getInitials(name) {
  const parts = String(name || '').replace(/[^A-Za-z ]/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function EstimateRow({ est, onOpen, onOpenHub, onApprove, onOpenJobOrder, onDelete, onReassign }) {
  const status     = est.status || 'draft';
  const cfg        = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const cust       = est.customer || {};
  const name       = (cust.name || '').trim();
  const isArchived = status === 'archived';
  const btn = {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '9px 14px', borderRadius: '9px',
    fontSize: '13px', fontWeight: '700', cursor: 'pointer',
    fontFamily: FONT_SANS, whiteSpace: 'nowrap',
  };

  return (
    <div
      onClick={onOpen}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.10)'; e.currentTarget.style.borderColor = '#d8ccbf'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)'; e.currentTarget.style.borderColor = '#e8e3dd'; }}
      style={{
        display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
        padding: '16px 18px', marginBottom: '10px',
        background: PAPER, borderRadius: '12px', cursor: 'pointer',
        border: '1px solid #e8e3dd', borderLeft: `5px solid ${cfg.color}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        opacity: isArchived ? 0.7 : 1, transition: 'box-shadow 0.15s, border-color 0.15s',
      }}
    >
      {/* Avatar */}
      <div style={{
        width: '44px', height: '44px', borderRadius: '50%', flexShrink: 0,
        background: WALNUT, color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '15px', fontWeight: '700',
      }}>{getInitials(name)}</div>

      {/* Who + what + when */}
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div style={{
          fontSize: '17px', fontWeight: '700', color: name ? INK : STONE,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{name || 'No customer name yet'}</div>
        <div style={{ fontSize: '14px', color: INK, marginTop: '3px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontWeight: '500' }}>{summarizeLines(est.lines)}</span>
          {cust.city && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: STONE }}>
              <MapPin size={13} strokeWidth={2} />{cust.city}
            </span>
          )}
          {cust.phone && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: STONE }}>
              <Phone size={13} strokeWidth={2} />{cust.phone}
            </span>
          )}
        </div>
        <div style={{ fontSize: '13px', color: STONE, marginTop: '4px' }}>
          Last saved {formatSavedAt(est.updatedAt || est.createdAt)}
          {onReassign && est.createdByName && <> · {est.createdByName}</>}
        </div>
      </div>

      {/* Status + amount */}
      <div style={{ textAlign: 'right', flexShrink: 0, minWidth: '120px' }}>
        <span style={{
          display: 'inline-block', fontSize: '13px', fontWeight: '700',
          color: cfg.color, background: cfg.bg,
          padding: '4px 12px', borderRadius: '999px',
        }}>{cfg.label}</span>
        <div style={{
          fontFamily: FONT_DISPLAY, fontSize: '20px', fontWeight: '600',
          color: INK, marginTop: '6px', whiteSpace: 'nowrap',
        }}>${fmtMoney(est.grandTotal || 0)}</div>
      </div>

      {/* Actions — every button has words, not just an icon */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}
        onClick={e => e.stopPropagation()}>
        <button onClick={onOpen} style={{ ...btn, background: WALNUT, color: 'white', border: `1.5px solid ${WALNUT}` }}>
          <Pencil size={15} strokeWidth={2} /> Open
        </button>
        {name && (
          <button onClick={onOpenHub} title="See every estimate for this customer"
            style={{ ...btn, background: 'white', color: WALNUT, border: `1.5px solid ${WALNUT}` }}>
            <Users size={15} strokeWidth={2} /> Customer page
          </button>
        )}
        {status === 'approved' ? (
          <button onClick={onOpenJobOrder} style={{ ...btn, background: '#EAF1EA', color: MOSS, border: '1.5px solid #B9D3BC' }}>
            <ClipboardCheck size={15} strokeWidth={2} /> {est.jobOrderCreated ? 'Job Order' : 'Create Job Order'}
          </button>
        ) : !isArchived && (
          <ApproveBtn onConfirm={onApprove} />
        )}
        {onReassign && (
          <button onClick={onReassign} style={{ ...btn, background: 'white', color: WALNUT, border: '1.5px solid #e0d5cc' }}>
            <UserRound size={15} strokeWidth={2} /> Reassign
          </button>
        )}
        <DeleteBtn onConfirm={onDelete} />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  SALES DASHBOARD                                                      */
/* ══════════════════════════════════════════════════════════════════════ */
function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function getFirstName(name) {
  if (!name) return '';
  return String(name).trim().split(/\s+/)[0];
}

function isThisMonth(ts) {
  if (!ts) return false;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

function countLineUnits(estimates, lineType) {
  let total = 0;
  for (const e of estimates) {
    for (const l of (e.lines || [])) {
      // Blank qty means "1" — new lines default to qty:'' and show a "1"
      // placeholder (see EMPTY_LINE in estimate-service.js), so an untouched
      // single-item line must count as 1, not 0. Matches the qty fallback
      // QuickQuotePage already uses for its own subtotal math.
      if (l.type === lineType) total += parseFloat(l.qty) || 1;
    }
  }
  return total;
}

function fmtMoneyShort(n) {
  const v = Math.round(n || 0);
  if (v >= 1000000) return '$' + (v / 1000000).toFixed(v >= 10000000 ? 0 : 1) + 'M';
  if (v >= 1000)    return '$' + (v / 1000).toFixed(v >= 10000 ? 0 : 1) + 'k';
  return '$' + v.toLocaleString('en-US');
}

/** Status dot + label — used wherever a compact status marker is needed
 *  instead of a filled uppercase pill (avoids the default-UI-kit chip look). */
function StatusDot({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: FONT_SANS }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
      <span style={{ fontSize: '13px', fontWeight: 600, color: cfg.color }}>{cfg.label}</span>
    </span>
  );
}

/** The one hero figure on the page — Approved $ — with the dollar pipeline
 *  (draft/sent/approved) as a small stacked bar beneath it, so the headline
 *  number and its funnel context live in one panel instead of two. */
function HeroStat({ value, segments, total }) {
  return (
    <div style={{
      background: WALNUT, borderRadius: '16px', padding: '26px 28px',
      flex: '0 0 300px', display: 'flex', flexDirection: 'column',
      justifyContent: 'space-between', color: 'white',
    }}>
      <div style={{ fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.72)', fontFamily: FONT_SANS }}>
        Approved this period
      </div>
      <div style={{
        fontFamily: FONT_DISPLAY, fontSize: '46px', fontWeight: 600,
        lineHeight: 1, marginTop: '10px', letterSpacing: '-0.01em',
      }}>{value}</div>

      {total > 0 && (
        <div style={{ marginTop: '22px' }}>
          <div style={{ display: 'flex', borderRadius: '5px', overflow: 'hidden', height: '6px', gap: '2px', marginBottom: '11px' }}>
            {segments.filter(s => s.value > 0).map(s => (
              <div key={s.label} style={{ flex: s.value / total, background: s.dot, minWidth: '3px' }} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {segments.map(s => (
              <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'rgba(255,255,255,0.75)', fontFamily: FONT_SANS }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
                {s.label}&nbsp;<span style={{ fontWeight: 700, color: 'white' }}>{fmtMoneyShort(s.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Supporting stats as a quiet inline strip — label/number pairs divided by
 *  hairlines, not four more boxed cards competing with the hero. */
function StatStrip({ items }) {
  return (
    <div style={{
      flex: '1 1 400px', background: PAPER, border: '1px solid #EAE2D8',
      borderRadius: '16px', display: 'flex', flexWrap: 'wrap',
    }}>
      {items.map((it, i) => (
        <div key={it.label} style={{
          flex: '1 1 120px', padding: '22px 20px',
          borderLeft: i > 0 ? '1px solid #EAE2D8' : 'none',
        }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: STONE, fontFamily: FONT_SANS }}>{it.label}</div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: '28px', fontWeight: 600, color: INK, letterSpacing: '-0.01em', marginTop: '4px' }}>{it.value}</div>
          {it.sub && <div style={{ fontSize: '11px', color: STONE, fontFamily: FONT_SANS, marginTop: '2px' }}>{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

function RecentRow({ est, onClick }) {
  const d = est.createdAt?.toDate ? est.createdAt.toDate() : new Date(est.createdAt || 0);
  const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const status  = est.status || 'draft';
  const total   = est.grandTotal || 0;

  return (
    <div onClick={onClick} style={{
      display: 'grid',
      gridTemplateColumns: '58px 1fr 92px 90px',
      alignItems: 'center', gap: '10px',
      padding: '9px 10px', borderRadius: '8px',
      cursor: 'pointer', fontFamily: FONT_SANS,
      transition: 'background 100ms',
    }}
    onMouseEnter={e => e.currentTarget.style.background = '#F5EFE8'}
    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <div style={{ fontSize: '11px', color: STONE, fontWeight: 600 }}>{dateStr}</div>
      <div style={{ fontSize: '13px', color: INK, fontWeight: 600,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {est.customer?.name || 'Unknown'}
      </div>
      <StatusDot status={status} />
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: '14px', textAlign: 'right', fontWeight: 600, color: WALNUT }}>
        {fmtMoneyShort(total)}
      </div>
    </div>
  );
}

/** Ranked leaderboard row — rank, name, approved $ (Fraunces), and a bar
 *  relative to the top performer, instead of a plain data-grid row. */
function TeamLeaderboardRow({ rank, rep, maxApproved }) {
  const pool = rep.sent + rep.approved;
  const conv = pool > 0 ? Math.round((rep.approved / pool) * 100) : 0;
  const barPct = maxApproved > 0 ? Math.round((rep.approvedSum / maxApproved) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 0', fontFamily: FONT_SANS }}>
      <div style={{ width: '16px', fontSize: '12px', fontWeight: 700, color: STONE, textAlign: 'center', flexShrink: 0 }}>
        {rank}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px', gap: '10px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rep.name}</span>
          <span style={{ fontFamily: FONT_DISPLAY, fontSize: '15px', fontWeight: 600, color: WALNUT, flexShrink: 0 }}>{fmtMoneyShort(rep.approvedSum)}</span>
        </div>
        <div style={{ height: '5px', background: '#EFE8DF', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: barPct + '%', background: MOSS, borderRadius: '3px' }} />
        </div>
      </div>
      <div style={{ width: '36px', textAlign: 'right', fontSize: '11px', color: STONE, flexShrink: 0 }}>{conv}%</div>
    </div>
  );
}

function SalesDashboard({ estimates, userProfile, currentUser, onOpenEstimate }) {
  const isOfficeManager = userProfile?.role === 'officeManager';
  const isSales         = userProfile?.role === 'sales';

  const [scope, setScope] = useState(() => {
    try { return localStorage.getItem('twp.dashboardScope') || 'month'; }
    catch { return 'month'; }
  });
  function setScopeAndPersist(v) {
    setScope(v);
    try { localStorage.setItem('twp.dashboardScope', v); } catch { /* private mode: just this visit */ }
  }

  // Filter by scope
  const scoped = scope === 'month'
    ? estimates.filter(e => isThisMonth(e.createdAt))
    : estimates;

  // KPIs
  const totalEst   = scoped.length;
  const sentCount     = scoped.filter(e => e.status === 'sent').length;
  const approvedCount = scoped.filter(e => e.status === 'approved').length;
  const approvedSum   = scoped
    .filter(e => e.status === 'approved')
    .reduce((s, e) => s + (e.grandTotal || 0), 0);
  const sentSum = scoped
    .filter(e => e.status === 'sent')
    .reduce((s, e) => s + (e.grandTotal || 0), 0);
  const draftSum = scoped
    .filter(e => !e.status || e.status === 'draft')
    .reduce((s, e) => s + (e.grandTotal || 0), 0);
  const pipelineDollarTotal = approvedSum + sentSum + draftSum;
  const conversion = sentCount + approvedCount > 0
    ? Math.round((approvedCount / (sentCount + approvedCount)) * 100)
    : 0;
  const doors      = countLineUnits(scoped, 'door');
  const twpWindows = countLineUnits(scoped, 'twp_window');

  // Recent activity — last 5 by createdAt
  const recent = [...scoped]
    .sort((a, b) => {
      const aT = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
      const bT = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
      return bT - aT;
    })
    .slice(0, 5);

  // Manager-only: per-rep breakdown
  const repBreakdown = isOfficeManager
    ? Object.values(scoped.reduce((acc, e) => {
        const uid  = e.createdByUid || 'unassigned';
        const name = e.createdByName || 'Unassigned';
        if (!acc[uid]) acc[uid] = { uid, name, count: 0, approvedSum: 0, sent: 0, approved: 0 };
        acc[uid].count++;
        if (e.status === 'approved') {
          acc[uid].approved++;
          acc[uid].approvedSum += (e.grandTotal || 0);
        } else if (e.status === 'sent') {
          acc[uid].sent++;
        }
        return acc;
      }, {})).sort((a, b) => b.approvedSum - a.approvedSum)
    : [];
  const maxApproved = repBreakdown.reduce((m, r) => Math.max(m, r.approvedSum), 0);

  const firstName = getFirstName(userProfile?.displayName || userProfile?.name || currentUser?.email);
  const today     = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  const pipelineSegments = [
    { label: 'Approved', value: approvedSum, dot: MOSS },
    { label: 'Sent',     value: sentSum,     dot: OCHRE },
    { label: 'Draft',    value: draftSum,    dot: STONE },
  ];

  return (
    <div style={{ marginBottom: '24px', fontFamily: FONT_SANS }}>

      {/* Greeting + scope toggle */}
      <div style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        marginBottom: '18px', flexWrap: 'wrap', gap: '12px',
      }}>
        <div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: '24px', fontWeight: 600, color: INK, lineHeight: 1.2 }}>
            {getGreeting()}, {firstName}.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px' }}>
            <span style={{ fontSize: '12px', color: STONE }}>{today}</span>
            {isOfficeManager && (
              <span style={{ fontSize: '10px', fontWeight: 600, color: WALNUT, background: '#FBF1E0', padding: '2px 9px', borderRadius: '10px' }}>
                All sales reps
              </span>
            )}
            {isSales && (
              <span style={{ fontSize: '10px', fontWeight: 600, color: WALNUT, background: '#FBF1E0', padding: '2px 9px', borderRadius: '10px' }}>
                Personal dashboard
              </span>
            )}
          </div>
        </div>

        <div style={{
          display: 'inline-flex', background: PAPER,
          border: '1px solid #EAE2D8', borderRadius: '8px', padding: '3px',
        }}>
          {[
            { v: 'month', label: 'This month' },
            { v: 'all',   label: 'All time'   },
          ].map(opt => (
            <button key={opt.v} onClick={() => setScopeAndPersist(opt.v)} style={{
              background: scope === opt.v ? WALNUT : 'transparent',
              color:      scope === opt.v ? 'white' : STONE,
              border: 'none', borderRadius: '6px',
              padding: '6px 14px', fontSize: '12px', fontWeight: '700',
              cursor: 'pointer', fontFamily: FONT_SANS,
              transition: 'all 150ms',
            }}>{opt.label}</button>
          ))}
        </div>
      </div>

      {/* Hero stat + supporting strip */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '18px', flexWrap: 'wrap' }}>
        <HeroStat value={fmtMoneyShort(approvedSum)} segments={pipelineSegments} total={pipelineDollarTotal} />
        <StatStrip items={[
          { label: 'Estimates',   value: totalEst },
          { label: 'Conversion',  value: conversion + '%', sub: sentCount + approvedCount === 0 ? 'No sent yet' : null },
          { label: 'Doors',       value: doors },
          { label: 'TWP Windows', value: twpWindows },
        ]} />
      </div>

      {/* Recent activity + manager-only team leaderboard */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isOfficeManager ? '1fr 1fr' : '1fr',
        gap: '32px',
      }}>
        {/* Recent — borderless list, reads as content not a boxed widget */}
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: INK, marginBottom: '10px' }}>
            Recent activity
          </div>
          {recent.length === 0 ? (
            <div style={{ fontSize: '13px', color: STONE, padding: '10px 0' }}>
              No estimates {scope === 'month' ? 'this month' : 'yet'}.
            </div>
          ) : recent.map(e => (
            <RecentRow key={e.id} est={e} onClick={() => onOpenEstimate(e)} />
          ))}
        </div>

        {/* Manager-only rep leaderboard */}
        {isOfficeManager && (
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: INK, marginBottom: '10px' }}>
              Sales team
            </div>
            {repBreakdown.length === 0 ? (
              <div style={{ fontSize: '13px', color: STONE }}>No activity yet.</div>
            ) : repBreakdown.map((r, i) => (
              <TeamLeaderboardRow key={r.uid} rank={i + 1} rep={r} maxApproved={maxApproved} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  REASSIGN MODAL                                                       */
/* ══════════════════════════════════════════════════════════════════════ */
function ReassignModal({ estimate, assignees, onConfirm, onCancel, loading }) {
  const [selectedUid, setSelectedUid] = useState(estimate?.createdByUid || '');

  if (!estimate) return null;

  const currentOwnerName = estimate.createdByName || 'Unassigned';
  const newOwner = assignees.find(u => u.uid === selectedUid);
  const willChange = selectedUid && selectedUid !== (estimate.createdByUid || '');

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000, padding: '20px',
    }}
    onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: '14px',
        width: '100%', maxWidth: '460px',
        padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontSize: '18px', fontWeight: '800', color: '#1a202c', marginBottom: '6px' }}>
          Reassign Estimate
        </div>
        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '18px' }}>
          {estimate.estimateId || 'Estimate'} for {estimate.customer?.name || 'Customer'}
        </div>

        <div style={{
          background: '#fbf9f6', border: '1px solid #e8e2da',
          borderRadius: '10px', padding: '12px 14px', marginBottom: '14px',
        }}>
          <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.06em', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>Currently Assigned To</div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#2d3748' }}>{currentOwnerName}</div>
        </div>

        <div style={{ marginBottom: '6px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.06em', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '6px' }}>Reassign To</div>
          <select
            value={selectedUid}
            onChange={e => setSelectedUid(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px',
              border: '1px solid #e0d5cc', borderRadius: '8px',
              background: 'white', color: '#2d3748',
              fontSize: '14px', fontWeight: '600',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <option value="">— Select user —</option>
            {assignees.map(u => (
              <option key={u.uid} value={u.uid}>
                {u.displayName} {u.role === 'officeManager' ? '(Office Manager)' : u.role === 'sales' ? '(Sales)' : ''}
              </option>
            ))}
          </select>
        </div>

        {willChange && (
          <div style={{
            marginTop: '14px', padding: '10px 12px',
            background: '#fef3c7', borderRadius: '8px',
            fontSize: '12px', color: '#92400e', fontWeight: '600',
          }}>
            The PDF &ldquo;Prepared By&rdquo; will update to {newOwner?.displayName || 'the new owner'}.
            They will see this estimate on their dashboard.
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={loading} style={{
            padding: '9px 18px', background: 'white',
            border: '1px solid #e0d5cc', borderRadius: '8px',
            color: '#4a5568', fontSize: '13px', fontWeight: '700',
            cursor: 'pointer', fontFamily: 'inherit',
          }}>Cancel</button>
          <button
            onClick={() => onConfirm(selectedUid)}
            disabled={loading || !willChange}
            style={{
              padding: '9px 18px', background: willChange ? WALNUT : '#d6cfc8',
              border: 'none', borderRadius: '8px',
              color: 'white', fontSize: '13px', fontWeight: '700',
              cursor: willChange ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
              opacity: loading ? 0.6 : 1,
            }}
          >{loading ? 'Reassigning…' : 'Reassign'}</button>
        </div>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════ */
/*  MAIN PAGE                                                            */
/* ══════════════════════════════════════════════════════════════════════ */
export default function EstimatesListPage() {
  const navigate = useNavigate();
  const { userProfile, currentUser } = useAuth();

  const [estimates, setEstimates]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  // Sales reps start on their Drafts — the quotes they're still working on
  const [filterStatus, setFilterStatus] = useState(() => userProfile?.role === 'sales' ? 'draft' : 'all');
  const [sortBy, setSortBy]             = useState('newest');
  const [showArchived, setShowArchived] = useState(false);
  const [page, setPage]                 = useState(1);
  const [reassignSource, setReassignSource] = useState(null);
  const [reassigning, setReassigning]       = useState(false);
  const [assignees, setAssignees]           = useState([]);
  const searchRef                       = useRef(null);
  const isSalesRole                     = userProfile?.role === 'sales';

  // Load once on open (loadEstimates reads the signed-in user, which is set before this page renders)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadEstimates(); }, []);

  // Reset to first page when filters/search change
  useEffect(() => { setPage(1); }, [search, filterStatus, sortBy, showArchived]);

  // Load assignees for the Reassign modal — only if user can reassign
  const canReassign = userProfile?.role === 'officeManager' || userProfile?.role === 'admin' || userProfile?.role === 'manager';
  useEffect(() => {
    if (!canReassign) return;
    getAssignableUsers().then(setAssignees).catch(e => console.error('Load assignees failed:', e));
  }, [canReassign]);

  async function handleReassignConfirm(newUid) {
    if (!reassignSource || !newUid) return;
    const newOwner = assignees.find(u => u.uid === newUid);
    if (!newOwner) return;
    setReassigning(true);
    try {
      await updateEstimate(reassignSource.id, {
        createdByUid:   newOwner.uid,
        createdByName:  newOwner.displayName,
        createdByPhone: newOwner.displayPhone,
        createdBy:      newOwner.email,
      });
      setReassignSource(null);
      // Refresh — reassigned estimate should disappear if AJ no longer owns it (for sales role)
      loadEstimates();
    } catch (e) {
      console.error('Reassign failed:', e);
      alert('Reassignment failed. Please try again.');
    }
    setReassigning(false);
  }

  // Press '/' anywhere to focus search
  useEffect(() => {
    function onKey(e) {
      if (e.key === '/'
        && document.activeElement?.tagName !== 'INPUT'
        && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  async function loadEstimates() {
    setLoading(true);
    try {
      // Salespersons see only their own estimates. Office manager / admin sees all.
      const isSales = userProfile?.role === 'sales';
      const uid     = isSales ? (currentUser?.uid || null) : null;
      setEstimates(await getEstimates(null, uid));
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function handleDelete(e, id) {
    e.stopPropagation();
    await deleteEstimate(id);
    setEstimates(prev => prev.filter(est => est.id !== id));
  }

  // Approve + jump straight to Job Order creation, right from the row —
  // no need to open the estimate editor first.
  async function handleApproveAndOpenJobOrder(e, est) {
    e.stopPropagation();
    const approvedAt = new Date();
    await updateEstimate(est.id, { status: 'approved', approvedAt });
    setEstimates(prev => prev.map(x => x.id === est.id ? { ...x, status: 'approved', approvedAt } : x));
    navigate(`/estimates/job-order/${est.id}`);
  }

  // Separate archived
  const activeEstimates   = estimates.filter(e => e.status !== 'archived');
  const archivedEstimates = estimates.filter(e => e.status === 'archived');
  const pool              = showArchived ? estimates : activeEstimates;

  // Search + filter
  const filtered = pool.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !search
      || e.customer?.name?.toLowerCase().includes(q)
      || (e.label || '').toLowerCase().includes(q)
      || e.estimateId?.toLowerCase().includes(q)
      || e.customer?.city?.toLowerCase().includes(q);
    const matchStatus = filterStatus === 'all' || (e.status || 'draft') === filterStatus;
    return matchSearch && matchStatus;
  });

  // One row per quote. "Last worked on" sorts by last save, so what a rep
  // touched most recently is always at the top.
  const lastTouched = e => {
    const t = e.updatedAt || e.createdAt;
    return t?.toDate ? t.toDate().getTime() : new Date(t || 0).getTime();
  };
  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'oldest':  return lastTouched(a) - lastTouched(b);
      case 'highest': return (b.grandTotal || 0) - (a.grandTotal || 0);
      case 'lowest':  return (a.grandTotal || 0) - (b.grandTotal || 0);
      case 'name':    return (a.customer?.name || '').localeCompare(b.customer?.name || '');
      default:        return lastTouched(b) - lastTouched(a);
    }
  });
  const PAGE_SIZE = 15;
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const stats = [
    { label: 'Drafts',   value: 'draft',    count: activeEstimates.filter(e => (e.status || 'draft') === 'draft').length, color: '#6B6157' },
    { label: 'Sent',     value: 'sent',     count: activeEstimates.filter(e => e.status === 'sent').length,     color: OCHRE },
    { label: 'Approved', value: 'approved', count: activeEstimates.filter(e => e.status === 'approved').length, color: MOSS },
    { label: 'All',      value: 'all',      count: activeEstimates.length,                                       color: WALNUT },
  ];

  const emptyMsg = {
    all:      { title: search ? 'No results found' : 'No estimates yet',
                sub: search ? `Nothing matched "${search}"` : 'Create your first quote for a customer.' },
    draft:    { title: search ? 'No drafts found' : 'No drafts right now',
                sub: search ? `No drafts matched "${search}". Try the All tab.` : 'Quotes you start show up here automatically.' },
    sent:     { title: 'Nothing sent yet',      sub: 'Send an estimate to a customer to see it here.' },
    approved: { title: 'No approved estimates', sub: 'Approve an estimate from the customer hub.' },
    declined: { title: 'No declined estimates', sub: 'Declined estimates will appear here.' },
  }[filterStatus] || { title: 'No estimates', sub: '' };

  const breadcrumb = 'Dashboard › Estimates';

  return (
    <AppLayout
      title="Estimates"
      breadcrumb={breadcrumb}
      topBarActions={
        <Button size="small" onClick={() => navigate('/estimates/new')}>+ New Quote</Button>
      }
    >
    <div style={{ fontFamily: FONT_SANS }}>

      {/* KPI dashboard — managers only; sales reps found it confusing */}
      {!loading && !isSalesRole && (
        <SalesDashboard
          estimates={activeEstimates}
          userProfile={userProfile}
          currentUser={currentUser}
          onOpenEstimate={(est) => navigate('/estimates/edit/' + est.id)}
        />
      )}

      <div style={{ paddingBottom: '24px' }}>

        {/* ── Status tabs — big and labeled ── */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
          {stats.map(s => {
            const on = filterStatus === s.value;
            return (
              <button key={s.value} onClick={() => setFilterStatus(s.value)}
                style={{
                  background: on ? s.color : 'white',
                  color: on ? 'white' : INK,
                  border: `1.5px solid ${on ? s.color : '#d8ccbf'}`,
                  borderRadius: '10px', padding: '10px 18px',
                  fontSize: '15px', fontWeight: '700',
                  cursor: 'pointer', fontFamily: FONT_SANS,
                  boxShadow: on ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                  transition: 'all 150ms',
                }}>
                {s.label}
                <span style={{
                  marginLeft: '8px', padding: '1px 8px', borderRadius: '999px', fontSize: '13px',
                  background: on ? 'rgba(255,255,255,0.25)' : '#F1EDE7',
                  color: on ? 'white' : STONE,
                }}>{s.count}</span>
              </button>
            );
          })}
        </div>

        {/* ── Search + sort + new quote ── */}
        <div style={{
          display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px',
        }}>
          <div style={{
            flex: '1 1 260px', display: 'flex', alignItems: 'center', gap: '10px',
            background: 'white', border: '1.5px solid #d8ccbf', borderRadius: '10px', padding: '10px 14px',
          }}>
            <Search size={18} strokeWidth={2} color={STONE} style={{ flexShrink: 0 }} />
            <input
              ref={searchRef}
              name="twp-estimates-search"
              autoComplete="off"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by customer name or city"
              style={{
                flex: 1, minWidth: 0, border: 'none', outline: 'none',
                fontSize: '15px', fontFamily: FONT_SANS,
                color: INK, background: 'transparent',
              }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{
                background: '#F1EDE7', border: 'none', color: INK, borderRadius: '6px',
                cursor: 'pointer', fontSize: '13px', fontWeight: '600', padding: '3px 10px', flexShrink: 0,
                fontFamily: FONT_SANS,
              }}>Clear</button>
            )}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <span style={{ fontSize: '14px', color: INK, fontWeight: '600' }}>Sort by</span>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              style={{
                border: '1.5px solid #d8ccbf', borderRadius: '10px',
                padding: '10px 12px', fontSize: '14px', fontFamily: FONT_SANS,
                color: INK, background: 'white', outline: 'none', cursor: 'pointer',
              }}
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>

          <button onClick={() => navigate('/estimates/new')} style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0,
            background: WALNUT, color: 'white', border: 'none', borderRadius: '10px',
            padding: '11px 20px', fontSize: '15px', fontWeight: '700',
            cursor: 'pointer', fontFamily: FONT_SANS, boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          }}><Plus size={17} strokeWidth={2.5} /> New Quote</button>
        </div>

        {/* ── Estimate list ── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: STONE, fontSize: '15px' }}>
            <Loader2 size={26} strokeWidth={2} style={{ marginBottom: '10px', animation: 'estimates-spin 0.8s linear infinite' }} />
            <div>Loading estimates…</div>
            <style>{'@keyframes estimates-spin { to { transform: rotate(360deg); } }'}</style>
          </div>
        ) : pageRows.length === 0 ? (
          <div style={{
            background: PAPER, borderRadius: '14px', padding: '48px 32px',
            textAlign: 'center', border: '1.5px dashed #d8ccbf',
          }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: '22px', fontWeight: 600, color: INK, marginBottom: '6px' }}>
              {emptyMsg.title}
            </div>
            <div style={{ color: STONE, fontSize: '15px', marginBottom: filterStatus !== 'all' ? '18px' : 0 }}>{emptyMsg.sub}</div>
            {filterStatus !== 'all' && (
              <button onClick={() => setFilterStatus('all')} style={{
                background: 'white', color: WALNUT, border: `1.5px solid ${WALNUT}`, borderRadius: '10px',
                padding: '10px 20px', fontSize: '14px', fontWeight: '700',
                cursor: 'pointer', fontFamily: FONT_SANS,
              }}>Show all {activeEstimates.length} estimates</button>
            )}
          </div>
        ) : (
          pageRows.map(est => (
            <EstimateRow
              key={est.id}
              est={est}
              onOpen={() => navigate(`/estimates/edit/${est.id}`)}
              onOpenHub={() => navigate(`/estimates/customer/${encodeURIComponent(est.customer?.name || '')}`)}
              onApprove={e => handleApproveAndOpenJobOrder(e, est)}
              onOpenJobOrder={() => navigate(`/estimates/job-order/${est.id}`)}
              onDelete={e => handleDelete(e, est.id)}
              onReassign={canReassign ? () => setReassignSource(est) : null}
            />
          ))
        )}

        {/* ── Pagination ── */}
        {!loading && pageCount > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '12px', marginTop: '20px', marginBottom: '8px', flexWrap: 'wrap',
          }}>
            {[
              { label: <><ChevronLeft size={16} strokeWidth={2} /> Previous</>, off: page === 1,         go: () => setPage(p => Math.max(1, p - 1)) },
              { label: <>Next <ChevronRight size={16} strokeWidth={2} /></>,    off: page >= pageCount, go: () => setPage(p => Math.min(pageCount, p + 1)) },
            ].map((b, i) => (
              <React.Fragment key={i}>
                {i === 1 && (
                  <div style={{ fontSize: '14px', fontWeight: '700', color: INK, fontFamily: FONT_SANS }}>
                    Page {page} of {pageCount}
                    <span style={{ color: STONE, fontWeight: '500', marginLeft: '8px' }}>({sorted.length} estimates)</span>
                  </div>
                )}
                <button onClick={b.go} disabled={b.off} style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: '10px 16px', borderRadius: '10px',
                  background: b.off ? '#f3f4f6' : 'white',
                  border: `1.5px solid ${b.off ? '#e5e7eb' : WALNUT}`,
                  color: b.off ? '#9ca3af' : WALNUT,
                  fontSize: '14px', fontWeight: '700',
                  cursor: b.off ? 'not-allowed' : 'pointer', fontFamily: FONT_SANS,
                }}>{b.label}</button>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* ── Archived footer hint (when hidden) ── */}
        {!loading && !showArchived && archivedEstimates.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <button
              onClick={() => setShowArchived(true)}
              style={{
                background: 'none', border: '1px dashed #d0c8c0', borderRadius: '10px',
                color: '#aaa', fontSize: '12px', fontWeight: '600',
                padding: '10px 22px', cursor: 'pointer', fontFamily: FONT_SANS,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = WALNUT; e.currentTarget.style.color = WALNUT; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#d0c8c0'; e.currentTarget.style.color = '#aaa'; }}
            >
              Show {archivedEstimates.length} archived estimate{archivedEstimates.length !== 1 ? 's' : ''}
            </button>
          </div>
        )}
      </div>

      {/* Reassign modal */}
      {reassignSource && (
        <ReassignModal
          estimate={reassignSource}
          assignees={assignees}
          loading={reassigning}
          onConfirm={handleReassignConfirm}
          onCancel={() => { if (!reassigning) setReassignSource(null); }}
        />
      )}
    </div>
    </AppLayout>
  );
}